'use client';

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import ChatWallpaper from './ChatWallpaper';
import MessageGroup from './MessageGroup';
import DateSeparator from './DateSeparator';
import TypingIndicator from './TypingIndicator';
import EmptyChatState from './EmptyChatState';
import { Message } from './types';

interface ChatMessageAreaProps {
  messages: Message[];
  isSelecting: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onContextMenu: (msgId: string, x: number, y: number) => void;
  onReply: (msg: Message) => void;
  isTyping: boolean;
  replyTo: Message | null;
  editingMessageId?: string | null;
  onSaveEdit?: (msgId: string, newText: string) => void;
  onCancelEdit?: () => void;
}

interface GroupedItem {
  type: 'separator' | 'group';
  date?: string;
  senderId?: 'me' | 'alex';
  messages?: Message[];
}

function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  const msgDateStr = `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
  const todayUTC = new Date('2026-08-29T00:00:00Z');
  const yesterdayUTC = new Date('2026-08-28T00:00:00Z');
  const todayStr = `${todayUTC.getUTCFullYear()}-${todayUTC.getUTCMonth()}-${todayUTC.getUTCDate()}`;
  const yesterdayStr = `${yesterdayUTC.getUTCFullYear()}-${yesterdayUTC.getUTCMonth()}-${yesterdayUTC.getUTCDate()}`;

  if (msgDateStr === todayStr) return 'Today';
  if (msgDateStr === yesterdayStr) return 'Yesterday';

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function groupMessages(messages: Message[]): GroupedItem[] {
  const items: GroupedItem[] = [];
  let lastDate = '';
  let currentGroup: Message[] | null = null;
  let currentSender: 'me' | 'alex' | null = null;

  for (const msg of messages) {
    const dateLabel = formatDateLabel(msg.timestamp);
    if (dateLabel !== lastDate) {
      if (currentGroup && currentGroup.length > 0) {
        items.push({ type: 'group', senderId: currentSender!, messages: currentGroup });
      }
      currentGroup = null;
      currentSender = null;
      items.push({ type: 'separator', date: dateLabel });
      lastDate = dateLabel;
    }
    if (msg.senderId !== currentSender) {
      if (currentGroup && currentGroup.length > 0) {
        items.push({ type: 'group', senderId: currentSender!, messages: currentGroup });
      }
      currentGroup = [msg];
      currentSender = msg.senderId;
    } else {
      currentGroup!.push(msg);
    }
  }
  if (currentGroup && currentGroup.length > 0) {
    items.push({ type: 'group', senderId: currentSender!, messages: currentGroup });
  }
  return items;
}

export default function ChatMessageArea({
  messages,
  isSelecting,
  selectedIds,
  onToggleSelect,
  onContextMenu,
  onReply,
  isTyping,
  editingMessageId,
  onSaveEdit,
  onCancelEdit,
}: ChatMessageAreaProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    scrollToBottom('instant');
  }, []);

  useEffect(() => {
    if (isNearBottom) {
      scrollToBottom('smooth');
    }
  }, [messages.length, isTyping]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const near = distFromBottom < 120;
    setIsNearBottom(near);
    setShowScrollBtn(distFromBottom > 200);
  }, []);

  const grouped = groupMessages(messages);

  return (
    <ChatWallpaper>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto scrollbar-thin px-2 sm:px-3 py-3"
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
      >
        {messages.length === 0 && <EmptyChatState />}

        <div className="max-w-3xl mx-auto w-full flex flex-col gap-1">
          {grouped.map((item, i) => {
            if (item.type === 'separator') {
              return <DateSeparator key={`sep-${item.date}-${i}`} label={item.date!} />;
            }
            return (
              <MessageGroup
                key={`group-${item.senderId}-${item.messages![0].id}`}
                messages={item.messages!}
                senderId={item.senderId!}
                isSelecting={isSelecting}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onContextMenu={onContextMenu}
                onReply={onReply}
                editingMessageId={editingMessageId}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
              />
            );
          })}

          {isTyping && <TypingIndicator />}

          <div ref={bottomRef} className="h-2" />
        </div>
      </div>

      {/* Scroll to bottom button */}
      <div
        className={`absolute bottom-4 right-4 sm:right-5 z-10 transition-all duration-200 ${showScrollBtn ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      >
        <button
          onClick={() => scrollToBottom('smooth')}
          className="scroll-to-bottom-btn w-[42px] h-[42px] rounded-full flex items-center justify-center transition-transform duration-150 active:scale-90 hover:scale-105 focus:outline-none focus:ring-2"
          style={{ focusRingColor: 'rgba(46,166,255,0.4)' } as React.CSSProperties}
          aria-label="Scroll to bottom"
          title="Scroll to latest messages"
        >
          <ChevronDown size={20} className="text-white" />
        </button>
      </div>
    </ChatWallpaper>
  );
}